import React, { useState, useEffect } from 'react';
import {
  Book,
  BookOpen,
  FileText,
  GraduationCap,
  PenLine,
  Pointer,
  Rocket,
  Star,
  Target,
  Timer,
  TriangleAlert,
} from 'lucide-react';
import { selectiveApi } from '../../services/selectiveApi';
import './SessionLauncher.css';

/**
 * SessionLauncher Component
 *
 * Allows users to configure and launch practice or mock exam sessions.
 * Provides filters for section, domains, tier, difficulty, question count, and time limit.
 *
 * NOTE: Section codes are uppercase (MATH, READ, THINK, WRIT) to match database schema.
 */
const SessionLauncher = ({ onSessionStart, onCancel, initialSessionType = 'practice', initialConfig = null }) => {
  // Session configuration state
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState(null); // Exam type ID
  const [sessionType, setSessionType] = useState(
    initialSessionType === 'mock_exam' ? 'mock_exam' : 'practice'
  );
  // initialConfig lets recommendations / suggested sessions pre-fill the launcher
  const initialSection = initialConfig?.section_code
    || (initialConfig?.domain_code ? initialConfig.domain_code.split('-')[0] : null);
  const [sectionCode, setSectionCode] = useState(initialSection || 'MATH'); // MATH, READ, THINK, WRIT
  const [selectedDomains, setSelectedDomains] = useState(
    initialConfig?.domain_code ? [initialConfig.domain_code] : []
  );
  const [tier, setTier] = useState(initialConfig?.tier || 'foundation'); // foundation, intermediate, advanced, mastery
  const [questionCount, setQuestionCount] = useState(initialConfig?.question_count || 10);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(30);

  // Data state
  const [availableDomains, setAvailableDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch exam types on mount
  useEffect(() => {
    fetchExamTypes();
  }, []);

  // Fetch domains when section or exam type changes
  useEffect(() => {
    if (selectedExamType) {
      fetchDomains();
    }
  }, [sectionCode, selectedExamType]);

  const fetchExamTypes = async () => {
    try {
      const types = await selectiveApi.questions.getExamTypes();
      setExamTypes(types);
      // Auto-select first exam type if available
      if (types.length > 0) {
        setSelectedExamType(types[0].id);
      }
    } catch (err) {
      console.error('Error fetching exam types:', err);
      setError('Failed to load exam types');
    }
  };

  const fetchDomains = async () => {
    try {
      const domains = await selectiveApi.questions.getDomains(sectionCode, selectedExamType);
      setAvailableDomains(domains);
      // Keep a pre-filled selection (from a recommendation) if it belongs to
      // this section; otherwise select ALL skills by default so students can
      // start immediately - narrowing down is the optional step.
      setSelectedDomains(prev => {
        const kept = prev.filter(code => domains.some(d => (d.domain_code || d.code) === code));
        return kept.length > 0 ? kept : domains.map(d => d.domain_code || d.code);
      });
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError('Failed to load available domains');
    }
  };

  const handleDomainToggle = (domainCode) => {
    setSelectedDomains(prev => {
      if (prev.includes(domainCode)) {
        // Remove this domain
        return prev.filter(d => d !== domainCode);
      } else {
        // Add this domain
        return [...prev, domainCode];
      }
    });
  };

  const handleSelectAllDomains = () => {
    if (selectedDomains.length === availableDomains.length && availableDomains.length > 0) {
      // Deselect all
      setSelectedDomains([]);
    } else {
      // Select all
      setSelectedDomains(availableDomains.map(d => d.domain_code || d.code));
    }
  };

  const handleStartSession = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate exam type is selected
      if (!selectedExamType) {
        setError('Please select an exam type');
        setLoading(false);
        return;
      }

      // Filter and clean domain list - remove null/undefined values
      const cleanDomains = selectedDomains.filter(d => d !== null && d !== undefined && d !== '');

      // Duration: at least 2 minutes per question, clamped to the DB range (5-180,
      // API max 150). Mock exams always use the computed exam-length duration.
      const minRequiredDuration = questionCount * 2;
      const clampDuration = (minutes) => Math.min(150, Math.max(15, minutes));

      let finalDuration;
      if (timeLimitEnabled && sessionType !== 'mock_exam') {
        finalDuration = clampDuration(Math.max(timeLimitMinutes, minRequiredDuration));
      } else {
        finalDuration = clampDuration(minRequiredDuration);
      }

      // Format data to match backend SessionCreate model
      // (API enum is 'practice' | 'mock_exam'; backend maps to DB values)
      const sessionData = {
        exam_type_id: selectedExamType, // CRITICAL: Prevents question leakage between exam types
        session_type: sessionType === 'mock_exam' ? 'mock_exam' : 'practice',
        selected_sections: [sectionCode], // Keep uppercase - DB has MATH, READ, THINK, WRIT
        selected_domains: cleanDomains, // Backend expects array of strings (can be empty)
        tier: tier,
        duration_minutes: finalDuration,
        question_count: questionCount
      };

      const response = await selectiveApi.sessions.start(sessionData);

      // Call parent callback with session data
      if (onSessionStart) {
        onSessionStart(response);
      }
    } catch (err) {
      console.error('Error starting session:', err);
      setError(err.message || 'Failed to start session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="session-launcher">
      <div className="launcher-header">
        <h2>{sessionType === 'mock_exam' ? 'Set Up Your Mock Exam' : 'Set Up Your Practice'}</h2>
        <p className="subtitle">
          {sessionType === 'mock_exam'
            ? 'Timed, exam-style conditions — just like the real test'
            : "Everything is ready to go — press Start, or fine-tune the options below"}
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon"><TriangleAlert size={20} className="inline-icon" /></span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="dismiss-error">×</button>
        </div>
      )}

      <div className="launcher-content">

        {/* Exam Type Selector */}
        <section className="config-section">
          <label className="section-label">1. Which exam are you preparing for?</label>
          <div className="exam-type-selector">
            {examTypes.map((examType) => (
              <button
                key={examType.id}
                className={`exam-type-button ${selectedExamType === examType.id ? 'active' : ''}`}
                onClick={() => setSelectedExamType(examType.id)}
              >
                <span className="exam-type-name">{examType.name}</span>
                <span className="exam-type-year">Year {examType.target_year_level}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Session Type */}
        <section className="config-section">
          <label className="section-label">2. How do you want to practise?</label>
          <div className="session-type-selector">
            <button
              className={`type-button ${sessionType === 'practice' ? 'active' : ''}`}
              onClick={() => setSessionType('practice')}
            >
              <span className="type-icon"><FileText size={28} /></span>
              <span className="type-name">Practice Mode</span>
              <span className="type-description">Immediate feedback, no time pressure</span>
            </button>
            <button
              className={`type-button ${sessionType === 'mock_exam' ? 'active' : ''}`}
              onClick={() => setSessionType('mock_exam')}
            >
              <span className="type-icon"><Target size={28} /></span>
              <span className="type-name">Mock Exam</span>
              <span className="type-description">Timed, realistic exam conditions</span>
            </button>
          </div>
        </section>

        {/* Section Selector */}
        <section className="config-section">
          <label className="section-label">3. Pick a subject</label>
          <div className="section-selector">
            <button
              className={`section-button ${sectionCode === 'MATH' ? 'active' : ''}`}
              onClick={() => setSectionCode('MATH')}
            >
              Mathematics
            </button>
            <button
              className={`section-button ${sectionCode === 'READ' ? 'active' : ''}`}
              onClick={() => setSectionCode('READ')}
            >
              Reading
            </button>
            <button
              className={`section-button ${sectionCode === 'THINK' ? 'active' : ''}`}
              onClick={() => setSectionCode('THINK')}
            >
              Thinking Skills
            </button>
          </div>
          <p className="section-note">
            <PenLine size={16} className="inline-icon" /> Writing practice has its own dedicated editor — use "Practice Writing" on the dashboard.
          </p>
        </section>

        {/* Domain Selector */}
        <section className="config-section">
          <div className="section-header">
            <label className="section-label">4. Skills to practise <span className="label-hint">(all selected — untick any you want to skip)</span></label>
            <button onClick={handleSelectAllDomains} className="select-all-button">
              {selectedDomains.length === availableDomains.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="domains-grid">
            {availableDomains.length === 0 ? (
              <p className="empty-state">Loading domains...</p>
            ) : (
              availableDomains.map(domain => {
                const domainCode = domain.domain_code || domain.code;
                return (
                  <label key={domainCode} className="domain-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDomains.includes(domainCode)}
                      onChange={() => handleDomainToggle(domainCode)}
                    />
                    <span className="domain-name">{domain.name}</span>
                    <span className="domain-code">{domainCode}</span>
                  </label>
                );
              })
            )}
          </div>
          <p className="selection-count">
            {selectedDomains.length} domain{selectedDomains.length !== 1 ? 's' : ''} selected
          </p>
        </section>

        {/* Tier Selector */}
        <section className="config-section">
          <label className="section-label">5. Choose your challenge level</label>
          <div className="tier-selector">
            <button
              className={`tier-button foundation ${tier === 'foundation' ? 'active' : ''}`}
              onClick={() => setTier('foundation')}
            >
              <span className="tier-icon"><Book size={22} /></span>
              Foundation
              <span className="tier-description">Build the basics</span>
            </button>
            <button
              className={`tier-button intermediate ${tier === 'intermediate' ? 'active' : ''}`}
              onClick={() => setTier('intermediate')}
            >
              <span className="tier-icon"><BookOpen size={22} /></span>
              Intermediate
              <span className="tier-description">Getting confident</span>
            </button>
            <button
              className={`tier-button advanced ${tier === 'advanced' ? 'active' : ''}`}
              onClick={() => setTier('advanced')}
            >
              <span className="tier-icon"><GraduationCap size={22} /></span>
              Advanced
              <span className="tier-description">Exam-level challenge</span>
            </button>
            <button
              className={`tier-button mastery ${tier === 'mastery' ? 'active' : ''}`}
              onClick={() => setTier('mastery')}
            >
              <span className="tier-icon"><Star size={22} /></span>
              Mastery
              <span className="tier-description">The toughest questions</span>
            </button>
          </div>
        </section>

        {/* Question Count Slider */}
        <section className="config-section">
          <label className="section-label">
            6. Number of questions: <strong>{questionCount}</strong>
            <span className="label-hint"> (about {questionCount * 2} minutes)</span>
          </label>
          <input
            type="range"
            min="5"
            max="50"
            step="5"
            value={questionCount}
            onChange={(e) => setQuestionCount(parseInt(e.target.value))}
            className="question-slider"
          />
          <div className="slider-labels">
            <span>5</span>
            <span>15</span>
            <span>25</span>
            <span>35</span>
            <span>50</span>
          </div>
        </section>

        {/* Time Limit - mock exams are always timed at 2 min/question */}
        {sessionType === 'mock_exam' ? (
          <section className="config-section">
            <label className="section-label">7. Time limit</label>
            <p className="section-note">
              <Timer size={16} className="inline-icon" /> Mock exams are always timed: <strong>{Math.min(150, Math.max(15, questionCount * 2))} minutes</strong> for {questionCount} questions — the same pace as the real exam.
            </p>
          </section>
        ) : (
          <section className="config-section">
            <label className="section-label checkbox-label">
              <input
                type="checkbox"
                checked={timeLimitEnabled}
                onChange={(e) => setTimeLimitEnabled(e.target.checked)}
              />
              <span>7. Set a custom time limit <span className="label-hint">(optional)</span></span>
            </label>
            {timeLimitEnabled && (
              <div className="time-limit-input">
                <input
                  type="number"
                  min="10"
                  max="120"
                  step="5"
                  value={timeLimitMinutes}
                  onChange={(e) => setTimeLimitMinutes(Math.max(10, parseInt(e.target.value)))}
                  className="time-input"
                />
                <span className="time-unit">minutes</span>
              </div>
            )}
          </section>
        )}

      </div>

      {/* Action Buttons */}
      <div className="launcher-footer">
        <button
          onClick={onCancel}
          className="cancel-button"
          disabled={loading}
        >
          Back to Dashboard
        </button>
        <button
          onClick={handleStartSession}
          className="start-button"
          disabled={loading || selectedDomains.length === 0}
        >
          {loading ? (
            <>
              <span className="spinner"></span>
              Starting Session...
            </>
          ) : (
            <>
              <span className="start-icon"><Rocket size={20} /></span>
              {sessionType === 'mock_exam' ? 'Start Mock Exam' : 'Start Practice'}
            </>
          )}
        </button>
      </div>

      {selectedDomains.length === 0 && !loading && (
        <p className="start-hint">
          <Pointer size={16} className="inline-icon" /> Tick at least one skill in step 4 to start
        </p>
      )}

      {/* Session Summary Preview */}
      {selectedDomains.length > 0 && (
        <div className="session-summary">
          <h3>Session Preview</h3>
          <ul>
            <li><strong>Type:</strong> {sessionType === 'practice' ? 'Practice Mode' : 'Mock Exam'}</li>
            <li><strong>Section:</strong> {getSectionName(sectionCode)}</li>
            <li><strong>Domains:</strong> {selectedDomains.length} selected</li>
            <li><strong>Tier:</strong> {tier.charAt(0).toUpperCase() + tier.slice(1)}</li>
            <li><strong>Questions:</strong> {questionCount}</li>
            <li><strong>Time Limit:</strong> {
              sessionType === 'mock_exam'
                ? `${Math.min(150, Math.max(15, questionCount * 2))} minutes (timed like the real exam)`
                : timeLimitEnabled ? `${timeLimitMinutes} minutes` : 'Take your time — no limit'
            }</li>
          </ul>
        </div>
      )}
    </div>
  );
};

// Helper function
const getSectionName = (code) => {
  const sections = {
    MATH: 'Mathematics',
    READ: 'Reading',
    THINK: 'Thinking Skills',
    WRITE: 'Writing'
  };
  return sections[code] || code;
};

export default SessionLauncher;
